import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Text, VStack, HStack, Flex, Badge, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, Input, Tooltip, CircularProgress, CircularProgressLabel, Grid, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Divider } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useGame } from '../../context/GameContext';
import { FaDharmachakra, FaCoins, FaGem, FaTrophy, FaUser, FaUserFriends, FaClock, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Mock player data - in a real app, this would come from a database or API
const mockPlayers = [
    { id: 1, name: "Player 1", color: "#805AD5", tickets: 60 }, // Purple - dramatically increased
    { id: 2, name: "Player 2", color: "#3B82F6", tickets: 30 }, // Blue - increased
    { id: 3, name: "Player 3", color: "#F97316", tickets: 10 }, // Orange - kept same
    { id: 4, name: "Player 4", color: "#EAB308", tickets: 5 },  // Yellow - kept same
    { id: 5, name: "Player 5", color: "#10B981", tickets: 2 },  // Green - further decreased
    { id: 6, name: "You", color: "#E53E3E", tickets: 0 }        // Red - current player
];

// Calculate total tickets and segment sizes
const calculateSegments = (players, currentPlayerTickets) => {
    // Update the current player's tickets
    const updatedPlayers = players.map(player => 
        player.name === "You" ? { ...player, tickets: currentPlayerTickets } : player
    );
    
    // Calculate total tickets
    const totalTickets = updatedPlayers.reduce((sum, player) => sum + player.tickets, 0);
    
    // Only include players with tickets
    const activePlayers = updatedPlayers.filter(player => player.tickets > 0);
    
    // Apply a much stronger power function to create dramatically different segment sizes
    // Using a power of 2.5 to create very noticeable differences
    const enhancedTickets = activePlayers.map(player => ({
        ...player,
        enhancedTickets: Math.pow(player.tickets, 2.5)
    }));
    
    // Calculate total of enhanced tickets
    const totalEnhancedTickets = enhancedTickets.reduce((sum, player) => sum + player.enhancedTickets, 0);
    
    // Calculate segment sizes (degrees) based on enhanced ticket allocation
    return enhancedTickets.map(player => ({
        ...player,
        percentage: (player.enhancedTickets / totalEnhancedTickets) * 100,
        degrees: (player.enhancedTickets / totalEnhancedTickets) * 360
    }));
};

const FortuneWheelInner = ({ isSpinning, spinDegrees, segments }) => {
    // Increased size for a bigger wheel
    const size = 900;
    const center = size / 2;
    const strokeWidth = 2;
    const radius = size / 2 - 30; // Reduced padding to make wheel bigger

    return (
        <Box
            position="relative"
            width="100%"
            height="100%"
            padding="20px" // Reduced padding
            background="#1A202C"
            borderRadius="50%"
            boxShadow="0 0 20px rgba(128, 90, 213, 0.4)"
            border="2px solid rgba(128, 90, 213, 0.6)"
        >
            <svg
                viewBox={`0 0 ${size} ${size}`}
                style={{
                    transform: `rotate(${spinDegrees}deg)`,
                    transition: isSpinning ? "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)" : "none",
                    filter: 'drop-shadow(0 0 10px rgba(128, 90, 213, 0.3))'
                }}
            >
                <defs>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                        <feMerge>
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                </defs>
                <g transform={`translate(${center},${center})`}>
                    {segments.length > 0 ? (
                        // Draw segments based on ticket allocation
                        segments.map((segment, index) => {
                            // Calculate start and end angles
                            let startAngle = 0;
                            for (let i = 0; i < index; i++) {
                                startAngle += segments[i].degrees;
                            }
                            const endAngle = startAngle + segment.degrees;
                            
                            // Convert to radians
                            const startRad = (startAngle) * Math.PI / 180;
                            const endRad = (endAngle) * Math.PI / 180;
                            
                            // Calculate arc points
                            const x1 = Math.cos(startRad) * radius;
                            const y1 = Math.sin(startRad) * radius;
                            const x2 = Math.cos(endRad) * radius;
                            const y2 = Math.sin(endRad) * radius;
                            
                            // For text placement
                            const midAngle = (startAngle + endAngle) / 2;
                            const midRad = midAngle * Math.PI / 180;
                            const textX = Math.cos(midRad) * (radius * 0.7);
                            const textY = Math.sin(midRad) * (radius * 0.7);
                            
                            // Large arc flag is 1 if the angle is > 180 degrees
                            const largeArcFlag = segment.degrees > 180 ? 1 : 0;
                            
                            return (
                                <g key={index}>
                                    <path
                                        d={`M 0 0 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                                        fill={segment.color}
                                        stroke="rgba(255, 255, 255, 0.3)"
                                        strokeWidth={strokeWidth}
                                        filter="url(#glow)"
                                    />
                                    {segment.degrees > 20 && ( // Only show text if segment is large enough
                                        <text
                                            x={textX}
                                            y={textY}
                                            fill="white"
                                            fontSize="16"
                                            fontWeight="bold"
                                            textAnchor="middle"
                                            alignmentBaseline="middle"
                                            transform={`rotate(${midAngle} ${textX} ${textY})`}
                                            style={{
                                                filter: 'drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.8))',
                                                textShadow: '0 0 10px rgba(0, 0, 0, 0.8)'
                                            }}
                                        >
                                            {segment.name}
                                        </text>
                                    )}
                                </g>
                            );
                        })
                    ) : (
                        // Empty wheel state
                        <circle 
                            cx="0" 
                            cy="0" 
                            r={radius} 
                            fill="#2D3748" 
                            stroke="rgba(255, 255, 255, 0.1)" 
                            strokeWidth={strokeWidth}
                        />
                    )}
                </g>
            </svg>

            {/* Prize Pod in Center */}
            <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width="180px"
                height="180px"
                borderRadius="50%"
                background="linear-gradient(135deg, #805AD5, #6B46C1)"
                border="4px solid rgba(255, 255, 255, 0.2)"
                boxShadow="0 0 30px rgba(128, 90, 213, 0.6)"
                zIndex={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                color="white"
                padding="4"
            >
                <FaTrophy size="40px" color="#F6E05E" style={{ marginBottom: '8px', filter: 'drop-shadow(0 0 10px rgba(246, 224, 94, 0.8))' }} />
                <Text fontSize="lg" fontWeight="bold" textAlign="center">PRIZE POD</Text>
                <Text fontSize="2xl" fontWeight="extrabold" color="#F6E05E" textShadow="0 0 10px rgba(246, 224, 94, 0.8)">
                    1000 JINU
                </Text>
            </Box>
        </Box>
    );
};

const TimeBox = ({ value, label, color = "purple.300" }) => (
    <Box
        bg="#060609"
        borderRadius="xl"
        p={{ base: 2, md: 4 }}
        minW={{ base: "70px", md: "100px" }}
        border="1px solid"
        borderColor="whiteAlpha.100"
        _hover={{
            transform: 'translateY(-5px)',
            boxShadow: `0 0 20px -5px ${color}`,
            borderColor: color,
        }}
        transition="all 0.3s ease-in-out"
        cursor="default"
    >
        <VStack spacing={1}>
            <Text 
                fontSize={{ base: "xl", md: "2xl" }} 
                fontWeight="bold" 
                color={color}
                transition="all 0.3s ease-in-out"
                _groupHover={{ textShadow: `0 0 10px ${color}` }}
            >
                {value}
            </Text>
            <Text 
                fontSize={{ base: "xs", md: "sm" }} 
                color="whiteAlpha.700"
                transition="all 0.3s ease-in-out"
                _groupHover={{ color: "whiteAlpha.900" }}
            >
                {label}
            </Text>
        </VStack>
    </Box>
);

const FortuneWheel = () => {
    const { gameState, addWinAmount, allocateTicketsToWheel } = useGame();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isSpinning, setIsSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [lastWin, setLastWin] = useState(null);
    const [ticketAmount, setTicketAmount] = useState(1);
    const [showAllocationPanel, setShowAllocationPanel] = useState(false);
    const [nextDrawDate, setNextDrawDate] = useState(null);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [showAllPlayers, setShowAllPlayers] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const playersPerPage = 10;

    // Calculate wheel segments based on ticket allocation
    const wheelSegments = calculateSegments(mockPlayers, gameState.wheelTickets);
    const totalTickets = wheelSegments.reduce((sum, segment) => sum + segment.tickets, 0);
    const yourSegment = wheelSegments.find(segment => segment.name === "You");
    const winChance = yourSegment ? (yourSegment.enhancedTickets / wheelSegments.reduce((sum, segment) => sum + segment.enhancedTickets, 0) * 100).toFixed(1) : 0;

    // Sort players by ticket count
    const sortedPlayers = [...wheelSegments].sort((a, b) => b.tickets - a.tickets);
    const totalPages = Math.ceil(sortedPlayers.length / playersPerPage);
    const currentPlayers = sortedPlayers.slice(
        (currentPage - 1) * playersPerPage,
        currentPage * playersPerPage
    );

    // Weekly draw timer for Friday 8 PM
    useEffect(() => {
        const calculateNextDrawDate = () => {
            const now = new Date();
            const nextDraw = new Date(now);
            
            // Set to next Friday 8 PM
            const currentDay = now.getDay(); // 0 = Sunday, 5 = Friday
            const daysUntilFriday = (5 - currentDay + 7) % 7;
            nextDraw.setDate(now.getDate() + daysUntilFriday);
            nextDraw.setHours(20, 0, 0, 0);
            
            // If it's already past 8 PM on Friday, move to next week
            if (now >= nextDraw) {
                nextDraw.setDate(nextDraw.getDate() + 7);
            }
            
            return nextDraw;
        };

        const updateTimer = () => {
            const now = new Date();
            const targetDate = nextDrawDate || calculateNextDrawDate();
            const timeDiff = targetDate.getTime() - now.getTime();
            
            if (timeDiff <= 0) {
                // Time for the draw!
                spinWheel();
                const newNextDraw = calculateNextDrawDate();
                setNextDrawDate(newNextDraw);
            } else {
                setTimeLeft({
                    days: Math.floor(timeDiff / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((timeDiff % (1000 * 60)) / 1000)
                });
            }
        };

        // Set initial next draw date and start timer
        if (!nextDrawDate) {
            setNextDrawDate(calculateNextDrawDate());
        }

        // Update timer every second
        updateTimer();
        const timer = setInterval(updateTimer, 1000);
        return () => clearInterval(timer);
    }, [nextDrawDate]);

    // Spin the wheel
    const spinWheel = () => {
        if (isSpinning || totalTickets === 0) return;
        
        setIsSpinning(true);
        const spins = 5; // Number of full rotations
        const extraDegrees = Math.random() * 360; // Random final position
        const totalDegrees = spins * 360 + extraDegrees;
        
        setRotation(rotation + totalDegrees);
        
        // Calculate which segment was landed on
        setTimeout(() => {
            const normalizedDegrees = (rotation + totalDegrees) % 360;
            
            // Determine which segment was landed on
            let currentAngle = 0;
            let winningSegment = null;
            
            for (const segment of wheelSegments) {
                if (normalizedDegrees >= currentAngle && normalizedDegrees < (currentAngle + segment.degrees)) {
                    winningSegment = segment;
                    break;
                }
                currentAngle += segment.degrees;
            }
            
            // If no segment was found or wheel is empty, default to a random one
            if (!winningSegment && wheelSegments.length > 0) {
                winningSegment = wheelSegments[Math.floor(Math.random() * wheelSegments.length)];
            }
            
            // Fixed prize of 1000 JINU if you win
            const winAmount = (winningSegment && winningSegment.name === "You") ? 1000 : 0;
            
            setLastWin({
                playerName: winningSegment ? winningSegment.name : "No one",
                isWinner: winningSegment && winningSegment.name === "You",
                totalWin: winAmount
            });
            
            // Add the win to the player's balance if they won
            if (winAmount > 0) {
                console.log(`Adding win amount: ${winAmount} JINU to player balance`);
                addWinAmount(winAmount);
            }
            
            setIsSpinning(false);
            onOpen(); // Show result modal
            
            // Automatically close the modal after 3 seconds
            setTimeout(() => {
                onClose();
            }, 3000);
        }, 5000);
    };
    
    const handleTicketAmountChange = (amount) => {
        // Ensure amount is between 1 and available tickets
        const availableTickets = gameState.ticketsCollected - gameState.wheelTickets;
        const newAmount = Math.max(1, Math.min(amount, availableTickets));
        setTicketAmount(newAmount);
    };
    
    const allocateTickets = () => {
        if (ticketAmount <= 0) return;
        
        // Allocate tickets to the wheel
        allocateTicketsToWheel(ticketAmount);
        
        // Reset ticket amount
        setTicketAmount(1);
        setShowAllocationPanel(false);
    };

    return (
        <Box 
            bg="gray.800" 
            borderRadius="xl" 
            p={6} 
            boxShadow="0 0 30px rgba(128, 90, 213, 0.3)"
            maxW="1580px"
            w="100%"
            mx="auto"
            position="relative"
            zIndex="1"
        >
            <VStack spacing={8} align="center">
                <VStack spacing={4} align="center">
                    <Text 
                        fontSize="3xl" 
                        color="purple.300" 
                        fontWeight="bold"
                        textAlign="center"
                        textShadow="0 0 10px rgba(128, 90, 213, 0.3)"
                    >
                        Weekly Fortune Draw
                    </Text>
                    
                    <HStack spacing={4} justify="center">
                        <TimeBox 
                            value={String(timeLeft.days).padStart(2, '0')} 
                            label="Days" 
                        />
                        <TimeBox 
                            value={String(timeLeft.hours).padStart(2, '0')} 
                            label="Hours" 
                        />
                        <TimeBox 
                            value={String(timeLeft.minutes).padStart(2, '0')} 
                            label="Minutes" 
                        />
                        <TimeBox 
                            value={String(timeLeft.seconds).padStart(2, '0')} 
                            label="Seconds" 
                        />
                    </HStack>
                </VStack>
                
                <Text fontSize="lg" color="gray.300" textAlign="center">
                    Allocate tickets for a chance to win 1000 JINU!
                </Text>

                <HStack spacing={4} mb={4}>
                    <Badge 
                        colorScheme="blue" 
                        fontSize="md" 
                        px={3} 
                        py={1} 
                        borderRadius="full"
                    >
                        Tickets Available: {gameState.ticketsCollected - gameState.wheelTickets}
                    </Badge>
                    
                    <Badge 
                        colorScheme="purple" 
                        fontSize="md" 
                        px={3} 
                        py={1} 
                        borderRadius="full"
                    >
                        Your Tickets: {gameState.wheelTickets}
                    </Badge>
                    
                    <Badge 
                        colorScheme="green" 
                        fontSize="md" 
                        px={3} 
                        py={1} 
                        borderRadius="full"
                    >
                        Win Chance: {winChance}%
                    </Badge>
                </HStack>
                
                {/* Main Wheel Container */}
                <Box 
                    position="relative" 
                    w={{ base: "500px", md: "700px" }} 
                    h={{ base: "500px", md: "700px" }}
                    mx="auto"
                >
                    {/* Pointer */}
                    <Box
                        position="absolute"
                        top="-5px"
                        left="50%"
                        transform="translateX(-50%)"
                        zIndex={2}
                        width={{ base: "30px", md: "40px" }}
                        height={{ base: "60px", md: "80px" }}
                        bg="white" 
                        clipPath="polygon(0% 0%, 100% 0%, 50% 100%)"
                        boxShadow="0 0 10px rgba(255, 255, 255, 0.7)"
                    />
                    
                    <FortuneWheelInner 
                        isSpinning={isSpinning} 
                        spinDegrees={rotation} 
                        segments={wheelSegments}
                    />
                </Box>
                
                {/* Allocate Tickets Button */}
                <HStack spacing={4}>
                    <Button
                        colorScheme="blue"
                        onClick={() => setShowAllocationPanel(true)}
                        leftIcon={<FaCoins />}
                        size="lg"
                        mt={4}
                        boxShadow="0 0 15px rgba(66, 153, 225, 0.5)"
                    >
                        Allocate Tickets
                    </Button>
                    
                    <Button
                        colorScheme="green"
                        onClick={() => {
                            const allTickets = gameState.ticketsCollected - gameState.wheelTickets;
                            if (allTickets > 0) {
                                allocateTicketsToWheel(allTickets);
                            }
                        }}
                        leftIcon={<FaCoins />}
                        size="lg"
                        mt={4}
                        boxShadow="0 0 15px rgba(72, 187, 120, 0.5)"
                        disabled={(gameState.ticketsCollected - gameState.wheelTickets) <= 0}
                    >
                        Allocate All Tickets
                    </Button>
                </HStack>
                
                {/* Player Legend with Pagination */}
                <Box 
                    bg="gray.700" 
                    p={4} 
                    borderRadius="lg" 
                    w="full" 
                    maxW="800px"
                >
                    <HStack justify="space-between" mb={3}>
                        <Text fontSize="lg" fontWeight="bold" color="purple.300">
                            Top Players in Wheel ({wheelSegments.length})
                        </Text>
                        <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="purple"
                            onClick={() => setShowAllPlayers(!showAllPlayers)}
                        >
                            {showAllPlayers ? 'Show Less' : 'Show All'}
                        </Button>
                    </HStack>

                    <Grid 
                        templateColumns={{ 
                            base: "repeat(2, 1fr)", 
                            md: showAllPlayers ? "repeat(3, 1fr)" : "repeat(4, 1fr)" 
                        }}
                        gap={3}
                        maxH={showAllPlayers ? "400px" : "160px"}
                        overflowY={showAllPlayers ? "auto" : "hidden"}
                        css={{
                            '&::-webkit-scrollbar': {
                                width: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(0,0,0,0.1)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(128, 90, 213, 0.5)',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: 'rgba(128, 90, 213, 0.7)',
                            },
                        }}
                    >
                        {currentPlayers.map((player, index) => (
                            <Badge
                                key={index}
                                px={3}
                                py={2}
                                borderRadius="full"
                                bg={player.color}
                                color="white"
                                display="flex"
                                alignItems="center"
                                gap={2}
                                fontSize="sm"
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                            >
                                {player.name === "You" ? <FaUser /> : <FaUserFriends />}
                                <Text as="span" fontWeight="bold">
                                    {player.name}
                                </Text>
                                <Text as="span" opacity={0.8}>
                                    {player.tickets}
                                </Text>
                            </Badge>
                        ))}
                    </Grid>

                    {/* Pagination Controls */}
                    {showAllPlayers && totalPages > 1 && (
                        <HStack justify="center" mt={4} spacing={2}>
                            <IconButton
                                icon={<FaChevronLeft />}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                size="sm"
                                variant="ghost"
                                colorScheme="purple"
                            />
                            <Text color="gray.300">
                                Page {currentPage} of {totalPages}
                            </Text>
                            <IconButton
                                icon={<FaChevronRight />}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                size="sm"
                                variant="ghost"
                                colorScheme="purple"
                            />
                        </HStack>
                    )}
                </Box>
                
                {/* Ticket Allocation Panel */}
                {showAllocationPanel && (
                    <Box
                        as={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        bg="gray.700"
                        p={4}
                        borderRadius="lg"
                        w="full"
                        maxW="500px"
                        boxShadow="0 0 20px rgba(0, 0, 0, 0.3)"
                        mt={4}
                    >
                        <VStack spacing={4}>
                            <Text fontSize="lg" fontWeight="bold" color="purple.300">
                                Allocate Tickets to Wheel
                            </Text>
                            
                            <Text fontSize="sm" color="gray.300">
                                More tickets = higher chance to win 1000 JINU!
                            </Text>
                            
                            <HStack w="100%" justify="space-between">
                                <Text fontSize="md" color="gray.300">Tickets to allocate:</Text>
                                <Text fontSize="xl" fontWeight="bold" color="blue.300">{ticketAmount}</Text>
                            </HStack>
                            
                            <Slider
                                aria-label="Allocate Tickets"
                                value={ticketAmount}
                                min={1}
                                max={Math.max(1, gameState.ticketsCollected - gameState.wheelTickets)}
                                onChange={(val) => handleTicketAmountChange(Math.round(val))}
                                colorScheme="blue"
                                mb={4}
                            >
                                <SliderTrack bg="gray.400" />
                                <SliderFilledTrack bg="blue.300" />
                                <SliderThumb boxSize={6} />
                            </Slider>
                            
                            <HStack spacing={4} w="100%" justify="space-between">
                                <Button
                                    size="sm"
                                    colorScheme="blue"
                                    variant="outline"
                                    onClick={() => handleTicketAmountChange(1)}
                                >
                                    Min (1)
                                </Button>
                                <Button
                                    size="sm"
                                    colorScheme="blue"
                                    variant="outline"
                                    onClick={() => handleTicketAmountChange(Math.floor((gameState.ticketsCollected - gameState.wheelTickets) / 2))}
                                >
                                    Half
                                </Button>
                                <Button
                                    size="sm"
                                    colorScheme="blue"
                                    variant="outline"
                                    onClick={() => handleTicketAmountChange(gameState.ticketsCollected - gameState.wheelTickets)}
                                >
                                    Max
                                </Button>
                            </HStack>
                            
                            <Divider my={3} borderColor="gray.600" />
                            
                            <HStack spacing={4} w="100%">
                                <Button
                                    colorScheme="gray"
                                    onClick={() => setShowAllocationPanel(false)}
                                    flex={1}
                                >
                                    Cancel
                                </Button>
                                
                                <Button
                                    colorScheme="blue"
                                    onClick={allocateTickets}
                                    disabled={ticketAmount <= 0 || (gameState.ticketsCollected - gameState.wheelTickets) < ticketAmount}
                                    flex={2}
                                >
                                    Allocate {ticketAmount} Tickets
                                </Button>
                            </HStack>
                            
                            <Button
                                colorScheme="green"
                                onClick={() => {
                                    const allTickets = gameState.ticketsCollected - gameState.wheelTickets;
                                    if (allTickets > 0) {
                                        allocateTicketsToWheel(allTickets);
                                        setShowAllocationPanel(false);
                                    }
                                }}
                                disabled={(gameState.ticketsCollected - gameState.wheelTickets) <= 0}
                                w="100%"
                                leftIcon={<FaCoins />}
                            >
                                Allocate All Available Tickets
                            </Button>
                        </VStack>
                    </Box>
                )}
            </VStack>
            
            {/* Win Result Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent bg="gray.900" color="white" borderRadius="xl" boxShadow="0 0 30px rgba(128, 90, 213, 0.5)">
                    <ModalHeader borderBottomWidth="1px" borderColor="purple.700" display="flex" alignItems="center">
                        <FaTrophy color="#F6E05E" style={{ marginRight: '10px' }} />
                        Wheel of Fortune Result
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={8} px={8}>
                        {lastWin && (
                            <VStack spacing={4} align="center">
                                <Text fontSize="xl" fontWeight="bold">
                                    {lastWin.playerName} won the spin!
                                </Text>
                                
                                {lastWin.isWinner ? (
                                    <>
                                        <Box
                                            as={motion.div}
                                            animate={{ 
                                                scale: [1, 1.1, 1],
                                                rotate: [0, 5, -5, 0]
                                            }}
                                            transition={{ 
                                                duration: 1.5,
                                                repeat: Infinity,
                                                repeatType: "loop"
                                            }}
                                            bg="purple.700"
                                            p={6}
                                            borderRadius="xl"
                                            boxShadow="0 0 30px rgba(128, 90, 213, 0.7)"
                                            w="full"
                                            textAlign="center"
                                        >
                                            <Text fontSize="2xl" fontWeight="bold" mb={2}>
                                                Congratulations!
                                            </Text>
                                            <Text fontSize="4xl" fontWeight="extrabold" color="#F6E05E" textShadow="0 0 10px rgba(246, 224, 94, 0.8)">
                                                +1000 JINU
                                            </Text>
                                        </Box>
                                        
                                        <Text color="gray.300">
                                            The prize has been added to your balance!
                                        </Text>
                                    </>
                                ) : (
                                    <Text fontSize="lg" color="gray.300">
                                        Better luck next time! Allocate more tickets to increase your chances.
                                    </Text>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="purple" onClick={onClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default FortuneWheel;
